/**
 * Popularité des SKILLCARDS pour l'admin (RPC `admin_card_stats`, migration 0730) :
 * élèves qui ont travaillé la carte, questions répondues, réussite, maîtrise moyenne,
 * comptes qui l'ont achetée, élèves à qui un prof l'a donnée, avis.
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { formatCardRating } from '@jeraisonne/db/cards';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export interface AdminCardStats {
  students: number;
  answers: number;
  correct: number;
  avgMastery: number | null;
  owners: number;
  assigned: number;
  ratingAvg: number | null;
  ratingCount: number;
}

interface RawStats {
  card_id: string;
  students: number | null;
  answers: number | null;
  correct: number | null;
  avg_mastery: number | string | null;
  owners: number | null;
  assigned: number | null;
  rating_avg: number | string | null;
  rating_count: number | null;
}

const num = (v: number | string | null): number | null => (v == null ? null : Number(v));

export function parseAdminCardStats(rows: RawStats[]): Map<string, AdminCardStats> {
  const map = new Map<string, AdminCardStats>();
  for (const r of rows) {
    map.set(r.card_id, {
      students: r.students ?? 0,
      answers: r.answers ?? 0,
      correct: r.correct ?? 0,
      avgMastery: num(r.avg_mastery),
      owners: r.owners ?? 0,
      assigned: r.assigned ?? 0,
      ratingAvg: num(r.rating_avg),
      ratingCount: r.rating_count ?? 0,
    });
  }
  return map;
}

export function useAdminCardStats(enabled: boolean) {
  return useQuery({
    queryKey: ['admin-card-stats'],
    enabled,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await db.rpc('admin_card_stats');
      if (error) throw error;
      return parseAdminCardStats((data ?? []) as RawStats[]);
    },
  });
}

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

/** Morceaux affichés sous la carte : « 12 élèves », « 340 réponses · 78 % justes »… */
export function adminCardStatsParts(s: AdminCardStats | undefined): string[] {
  if (!s) return [];
  const parts = [plural(s.students, 'élève', 'élèves')];
  parts.push(
    s.answers > 0
      ? `${plural(s.answers, 'réponse', 'réponses')} · ${Math.round((100 * s.correct) / s.answers)} % justes`
      : '0 réponse',
  );
  if (s.avgMastery != null && s.students > 0) {
    parts.push(`maîtrise ${Math.round(s.avgMastery)} %`);
  }
  if (s.owners > 0) parts.push(plural(s.owners, 'achat', 'achats'));
  if (s.assigned > 0) parts.push(`donnée à ${plural(s.assigned, 'élève', 'élèves')}`);
  const rating = formatCardRating(s.ratingAvg);
  if (rating && s.ratingCount > 0) parts.push(`${rating}/5 (${s.ratingCount} avis)`);
  return parts;
}

export const ADMIN_CARD_SORTS = [
  { value: 'default', label: 'Ordre habituel' },
  { value: 'students', label: 'Plus d’élèves' },
  { value: 'answers', label: 'Plus de réponses' },
  { value: 'owners', label: 'Plus d’achats' },
  { value: 'rating', label: 'Mieux notées' },
] as const;

export type AdminCardSort = (typeof ADMIN_CARD_SORTS)[number]['value'];

function sortValue(s: AdminCardStats | undefined, sort: AdminCardSort): number {
  if (!s) return -1;
  switch (sort) {
    case 'students':
      return s.students;
    case 'answers':
      return s.answers;
    case 'owners':
      return s.owners;
    case 'rating':
      return s.ratingCount > 0 ? (s.ratingAvg ?? 0) * 1000 + s.ratingCount : -1;
    default:
      return 0;
  }
}

/** Trie les cartes par popularité (tri stable : l'ordre habituel départage). */
export function sortCardsByStats<T extends { id: string }>(
  cards: T[],
  stats: Map<string, AdminCardStats> | undefined,
  sort: AdminCardSort,
): T[] {
  if (sort === 'default' || !stats) return cards;
  return cards
    .map((c, i) => ({ c, i, v: sortValue(stats.get(c.id), sort) }))
    .sort((a, b) => b.v - a.v || a.i - b.i)
    .map((x) => x.c);
}
